import {Container, Divider, Stack, Typography} from '@mui/material';
import React, {Fragment, useState} from 'react';

import {makeDeleteRefundUploadFile, makePostRefundUploadFile} from 'main/factories/usecases';
import {
	AccordionData,
	AlertInfo,
	CTAButtonFixed,
	MainFooter,
	StyledGrid,
} from 'presentation/components';
import {TermsNamesEnum} from 'presentation/hooks';
import {useMediaQueries} from 'presentation/pages/private/hooks';
import {UploadRefundFiles} from 'presentation/pages/private/refund/components';
import {useSendAdditionalDocs} from 'presentation/pages/private/refund/hooks';
import {translations} from 'presentation/translations-new';

import {RefundTaggingEnum} from 'main/analytics/gaTagsEnum';
import {UploadModals} from 'presentation/pages/private/refund/components/UploadModals';
import {useUploadRefundFile} from 'presentation/pages/private/refund/components/UploadRefundFiles/hooks/useUploadRefundFile';
import {useSendAdditionalDocsDataStore} from '../../store/useSendAdditionalDocsDataStore';
import {StyledFooterContainer} from './styles';

const {title, description} =
	translations['pt-br'].privateFlow.refund.additionalDocuments.refundInfo;
const {advance} = translations['pt-br'].privateFlow.refund.additionalDocuments.sendDocuments;
const {error} = translations['pt-br'].others.uploadFiles;
const {alertInfo} = translations['pt-br'].privateFlow.refund.additionalDocuments;

export const SendDocuments = () => {
	const {handleNextStep} = useSendAdditionalDocs();
	const {isTablet} = useMediaQueries();
	const sendAdditionalDocsData = useSendAdditionalDocsDataStore(state => state.data);
	const setSendAdditionalDocsData = useSendAdditionalDocsDataStore(state => state.setData);
	const [errorMessageState, setErrorMessageState] = useState<string | null>(null);
	const [sendingAttachState, setSendingAttachState] = useState<boolean>(false);

	const {
		uploadedFiles,
		deleteFile,
		handleUpload,
		IS_DELETING,
		IS_UPLOADING,
		blockCloseModal,
		IS_DELETING_ERROR,
		resetDeleteError,
	} = useUploadRefundFile({
		protocolCode: Number(sendAdditionalDocsData.protocolCode),
		requestCode: Number(sendAdditionalDocsData.requestCode),
		postFactory: makePostRefundUploadFile,
		deleteFactory: makeDeleteRefundUploadFile,
	});

	const someFileIsUploaded = uploadedFiles?.some(file => file.uploaded === true);

	const handleGoToReview = () => {
		if (IS_UPLOADING) {
			setSendingAttachState(true);
			return;
		}

		if (!someFileIsUploaded) {
			setErrorMessageState(error);
			return;
		}

		setSendAdditionalDocsData({...sendAdditionalDocsData, files: uploadedFiles});
		handleNextStep();
	};

	return (
		<Fragment>
			<Container>
				<StyledGrid.Root mt={isTablet ? 0 : 4}>
					<StyledGrid.Item md={12} lg={12}>
						<AccordionData
							title={title}
							subtitle={`Nº ${sendAdditionalDocsData?.request}`}
							disableGutters>
							<Fragment>
								<Divider sx={{mb: 2}} />
								<Stack gap={1}>
									<Typography
										variant="body2"
										color="text.secondary"
										fontWeight={600}>
										{description}
									</Typography>
									<Typography sx={{overflowWrap: 'break-word'}}>
										{sendAdditionalDocsData?.observations}
									</Typography>
								</Stack>
							</Fragment>
						</AccordionData>
					</StyledGrid.Item>
					<StyledGrid.Item sx={{background: 'transparent'}} md={12} lg={12}>
						<Stack gap={2} data-tag-id={RefundTaggingEnum.CLICK_DIV_UPLOAD_ARQUIVOS}>
							<AlertInfo.Root severity="info">
								<AlertInfo.Title
									variant="body2"
									sx={{fontWeight: 400, margin: 0}}
									title={alertInfo}
								/>
							</AlertInfo.Root>

							<UploadRefundFiles
								dataTagId={
									RefundTaggingEnum.CLICK_ADICIONAR_ARQUIVOS_ENVIO_DE_DOCUMENTOS_COMPLEMENTARES
								}
								removeFile={deleteFile}
								shouldShowTermsModal={{
									show: true,
									termName: TermsNamesEnum.UPLOAD_FILES,
								}}
								onChangeFile={handleUpload}
								values={uploadedFiles}
								isUploading={IS_UPLOADING}
								externalError={errorMessageState}
								setExternaErrorMessage={setErrorMessageState}
							/>
						</Stack>

						<Stack direction="row" justifyContent="flex-end" mt={2}>
							<CTAButtonFixed.Root>
								<CTAButtonFixed.Action
									data-tag-id={
										RefundTaggingEnum.CLICK_PROSSEGUIR_PARA_REVISAO_ENVIO_DE_DOCUMENTOS_COMPLEMENTARES
									}
									size="large"
									onClick={handleGoToReview}>
									{advance}
								</CTAButtonFixed.Action>
							</CTAButtonFixed.Root>
						</Stack>
					</StyledGrid.Item>
				</StyledGrid.Root>
			</Container>

			<StyledFooterContainer>
				<MainFooter />
			</StyledFooterContainer>

			<UploadModals
				openErrorModal={IS_DELETING_ERROR}
				closeErrorModa={resetDeleteError}
				openSendingAttachModal={sendingAttachState}
				closeSendingAttachModal={() => setSendingAttachState(false)}
				openPendingDialog={IS_DELETING}
				closePendingDialog={blockCloseModal}
			/>
		</Fragment>
	);
};
